import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, EMPTY, Observable, Subject, Subscription } from "rxjs";
import { catchError, filter, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedTenantState,
} from "src/app/store/reducers";

import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";
import { ProductService } from "src/app/shared/services/products/products/product.service";

import { SiteProductDTO } from "src/app/models/Sites/siteProductDTO";
import { ProductDTO } from "src/app/models/Products/productDTO";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { FieldType, FilterName } from "src/app/models/custom-table/custom-table.model";
import {
  Dialog,
  GuardConfirmationDialogData,
} from "src/app/models/dialog/dialog.model";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";

import { FormUtils } from "src/app/shared/utils/formUtils";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";

import { BaseSiteProducts } from "src/app/shared/base-classes/base-site-products/base-site-products";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { CanComponentDeactivate } from "src/app/shared";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";
import { SitesService } from "src/app/shared/services/sites/sites.service";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";

// Product Detail Form
const PRODUCT_FORM_PARAMS = {
  productList: "productList",
  siteCode: "siteCode",
  product: "product",
  productDescription: "productDescription",
  fccProductCode: "fccProductCode",
  shouldBeLinked: "shouldBeLinked"
};

@Component({
  selector: "site-products",
  templateUrl: "./products.component.html",
  styleUrls: ["./products.component.scss"],
})
export class ProductsComponent
  extends BaseSiteProducts
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  @Input() set siteCode(code: string) {
    if (code) {
      this._siteCode = code;
      this.createProductForm();
      this.loadFuelProductList(this._siteCode)
    }
  }
  @Input() duplication: boolean;

  @Output() getProductsForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  _siteCode: string;

  protected destroy$ = new Subject<void>();

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;

  // Site Product List
  siteFuelProductList$: Observable<SiteProductDTO[]>;
  siteFuelProductList: SiteProductDTO[];
  siteFuelProductListSub: Subscription;

  // Product Search
  isLoadingProducts$ = new BehaviorSubject<boolean>(false);
  productList$: Observable<ProductDTO[]>;
  productList: AutocompleteSelectItem[] = [];
  searchTimer: number;
  
  initialProductList: SiteProductDTO[] = [];
  private removedProducts: SiteProductDTO[] = [];

  // Tenant Data
  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  tenantDataFormat: any;

  isFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: true,
  };

  productDataForm: FormGroup;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private productService: ProductService,
    public dialog: MatDialog,
    public rolePermissionsService: RolePermissionsService,
    private sitesService: SitesService
  ) {
    super(dialog);

    this.tenantData$ = this.store.pipe(select(loadedTenantState));
  }

  ngOnInit() {
    this.loadTenantDateFomat();
    this.loadFuelProductList(this._siteCode)
  }

  loadTenantDateFomat() {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.tenantDataFormat = data;
      }
    });
  }

  loadFuelProductList(code: string) {
    this.sitesService.getSiteFuelProductList(code).pipe(
      map((result) =>{
        const products = result.items;
        this.populateProducts(products)
        this.checkPermissions();
        return products
      }
      ),
      catchError((error) => {
        this.snackbarService.show({
          message: this.translate.instant(error),
          type: SnackbarType.ERROR
        });
        return EMPTY;
      })
    ).subscribe()
  }

  createProductForm() {
    this.productDataForm = this.fb.group({
      [PRODUCT_FORM_PARAMS.productList]: this.fb.array([]),
    });
  }

  populateProducts(productList: SiteProductDTO[]) {
    this.initialProductList = productList.map((p) => ({
      ... p,
      fccProductCode: p.fccProductCode?.toString()
    }));
   
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );
    this.clearFormArray(products);
    productList.forEach((product) => {
      const productFormGroup = this.createProductFormGroup(product);
      products.push(productFormGroup);
    });
    
  }

  createProductFormGroup(product?: SiteProductDTO): FormGroup {
    const {
      siteCode,
      productCode,
      productDescription,
      fccProductCode,
      unitPriceGross,
    } = product || {};
    return this.fb.group({
      [PRODUCT_FORM_PARAMS.siteCode]: [siteCode ?? this._siteCode],
      [PRODUCT_FORM_PARAMS.product]: [
        productCode
          ? {
              code: productCode,
              description: productDescription,
              unitPriceGross,
            }
          : null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [PRODUCT_FORM_PARAMS.fccProductCode]: [
        fccProductCode ?? null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [PRODUCT_FORM_PARAMS.shouldBeLinked]: [ null ]
    });
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission("SITE_MANAGEMENT", "WRITE")
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if (!canEdit) {
          this.disableFormControls(this.productDataForm);
        }
      });

    this.canWrite$ = this.rolePermissionsService.hasPermission(
      "SITE_MANAGEMENT",
      "WRITE"
    );
  }

  protected disableFormControls(form: FormGroup): void {
    const productList = form.get(PRODUCT_FORM_PARAMS.productList);

    if (productList instanceof FormArray) {
      productList.controls.forEach((group: FormGroup) => {
        Object.keys(group.controls).forEach((controlName) => {
          group.get(controlName)?.disable();
        });
      });
    }
  }

  getProductsFormPristine() {
    this.getProductsForm.emit(this.productDataForm);
  }

  addProduct() {
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );

    const productFormGroup = this.createProductFormGroup();
    products.push(productFormGroup);
    this.productDataForm.markAsDirty();
  }

  openRemoveDialog(index: number) {
    this.openDefaultDialog(index, this.removeProduct.bind(this));
  }

  removeProduct(index: number) {
    const products = this.getFormArray(
      this.productDataForm,
      PRODUCT_FORM_PARAMS.productList
    );
    const removedValue = products.at(index).value;
    products.removeAt(index);
    
    const wasInitiallyLinked = this.initialProductList.some(
      (p) => p.fccProductCode === removedValue.fccProductCode?.toString()
    );

    if(wasInitiallyLinked) {
      this.removedProducts.push({
        siteCode: removedValue.siteCode,
        productCode: removedValue.product.code,
        fccProductCode: removedValue.fccProductCode?.toString(),
        shouldBeLinked: false
      })
    }
    this.productDataForm.markAsDirty();
    this.getProductsFormPristine();
  }

  validateProductSearch() {
    this.searchProducts("");
  }

  searchProducts(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm =
      typeof event === "string"
        ? event
        : (event.target as HTMLInputElement).value;

    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.isLoadingProducts$.next(true);
        this.productList$ = this.productService
          .getProductSearch(
            null,
            {
              filterConfig: [{ field: "code" }, { field: "description" }],
              searchTerms: [searchTerm],
            },
            null,
            {
              advancedFilter: [
                {
                  column: "isFuel",
                  operator: FilterName.EQUALS_TO,
                  value: true,
                },
              ],
            }
          )
          .pipe(
            map(({ items }) => items),
            catchError((error) => {
              this.snackbarService.show({
                message: this.translate.instant("Products.loadError"),
                type: SnackbarType.WARNING,
              });
              return EMPTY;
            }),
            finalize(() => this.isLoadingProducts$.next(false)),
            shareReplay()
          );
      },
      hasTimer ? 500 : 0
    );
  }

  displayProduct(product: { code: string; description: string }): string {
    return product?.description ?? "";
  }

  saveProductList() {
    if (this.productDataForm.pristine) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (!this.productDataForm.valid) {
      this.productDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant(
          "SiteManagement.SiteProductFormInvalid"
        ),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const currentProductList = this.productDataForm
    .get(PRODUCT_FORM_PARAMS.productList)
    .value.map((siteProduct) => ({
      siteCode: siteProduct.siteCode,
      productCode: siteProduct.product.code,
      fccProductCode: siteProduct.fccProductCode.toString()
    }));

    const addedProducts: SiteProductDTO[] =  currentProductList
      .filter((p) => !this.initialProductList.some((ip) => ip.fccProductCode === p.fccProductCode))
      .map((p) => ({ ... p, shouldBeLinked: true}))

    const siteProductList: SiteProductDTO[] = [...addedProducts, ...this.removedProducts]

    if (this.repeatedProducts(currentProductList)) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.RepeatRule"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (siteProductList) {
      this.sitesService
        .updateSiteStoreProductList({
          siteCode: this._siteCode,
          productsToLinkToSite: siteProductList
        })
        .pipe(
          tap(() => {

            this.store.dispatch(
            setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant(
                    "SiteManagement.siteProducts.saveProductSuccess"
                  ),
                  SnackbarType.SUCCESS
                )
              )
            );

            this.initialProductList = currentProductList;
            this.removedProducts = [];

            this.productDataForm.markAsUntouched();
            this.productDataForm.markAsPristine();
            this.getProductsFormPristine();
          }),
          catchError((err) => {
            this.store.dispatch(
              setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant(
                    "SiteManagement.siteProducts.saveProductError"
                  ),
                  SnackbarType.ERROR
                )
              )
            );
            return EMPTY;
          }),
        ).subscribe();
    }
  }

  repeatedProducts(siteProductList: SiteProductDTO[]): boolean {
    return siteProductList.some((productEl) => {
      const filteredProducts = siteProductList.filter(
        (product) =>
          product.productCode === productEl.productCode ||
          product.fccProductCode === productEl.fccProductCode
      );
      return filteredProducts.length > 1;
    });
  }

  ngOnDestroy() {
    if (this.siteFuelProductListSub) {
      this.siteFuelProductListSub.unsubscribe();
    }
    if (this.tenantDataSub) {
      this.tenantDataSub.unsubscribe();
    }
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.productDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
